"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { grantConsent } from "@/lib/marketing/events";
import { COOKIE_CONSENT } from "@/lib/marketing/utm";

/**
 * Minimal cookie/analytics consent banner.
 * Sets tlr_consent=1 on accept, which unlocks first-party event tracking
 * and flushes any buffered events.
 *
 * NOTE (taliora-west): Yandex Metrica removed in the western fork. When GA4/
 * Plausible lands, gate its load on the same consent (or switch to cookieless
 * Plausible and drop the banner per GDPR/CCPA — see WEST-FORK.md).
 */
export function ConsentBanner() {
  const t = useTranslations("consentBanner");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const granted = document.cookie.includes(`${COOKIE_CONSENT}=1`);
    const declined = document.cookie.includes(`${COOKIE_CONSENT}=0`);
    if (!granted && !declined) setVisible(true);
    if (granted) grantConsent();
  }, []);

  function accept() {
    const maxAge = 60 * 60 * 24 * 180;
    document.cookie = `${COOKIE_CONSENT}=1; path=/; max-age=${maxAge}; samesite=lax`;
    grantConsent();
    setVisible(false);
  }

  function decline() {
    const maxAge = 60 * 60 * 24 * 30;
    document.cookie = `${COOKIE_CONSENT}=0; path=/; max-age=${maxAge}; samesite=lax`;
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-50"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="bg-white border border-stone-200 rounded-2xl shadow-lg p-4">
        <p className="text-xs text-stone-600 leading-relaxed mb-3">
          {t.rich("text", {
            link: (c) => <a href="/legal/privacy" className="underline text-violet-600">{c}</a>,
          })}
        </p>
        <div className="flex items-stretch gap-2">
          <button
            onClick={accept}
            className="flex-1 bg-violet-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-violet-700 whitespace-nowrap"
          >
            {t("accept")}
          </button>
          <button
            onClick={decline}
            className="flex-1 text-xs text-stone-600 hover:text-stone-800 px-3 py-2 rounded-lg border border-stone-200 hover:border-stone-300 whitespace-nowrap"
          >
            {t("onlyNecessary")}
          </button>
        </div>
      </div>
    </div>
  );
}
